/* Right contextual editor — every form kind, field by field, ported
   1:1 from the vanilla renderForm / *Form builders.

   Mutation model: this component is keyed by `${selPath}:${formRev}`
   in EditorView. Text inputs are uncontrolled (defaultValue + onInput)
   and mutate the working copy in place — no re-render per keystroke.
   Every STRUCTURAL change (list add/remove/move, color pick, type/role
   switch, block add/remove) mutates in place then calls bumpForm(),
   which bumps formRev and remounts this whole tree so every input is
   rebuilt with fresh defaults (exactly like the SPA's innerHTML='').
   Navigation into a child calls selectPath (changes the key too). */

import React, { useCallback, useRef } from 'react';
import {
  PALETTE,
  autoGrow,
  ensureArr,
  swap,
  resolvePath,
  pathKind,
  nodeTitlePreview,
  defaultNode,
  defaultLane,
  defaultFinal,
  defaultBranch,
  defaultChip,
  defaultBlock,
} from './utils';
import RichText from './RichText';
import { phaseLabel, PHASE_LABEL_LIMIT } from './phaseHeader';

interface Props {
  workingRef: React.MutableRefObject<any>;
  selPath: string;
  markDirty: () => void;
  bumpForm: () => void;
  selectPath: (path: string) => void;
}

interface InputOpts {
  type?: string;
  placeholder?: string;
  /** Bold-capable field (RichText) — only for strings the poster renders
      through its markup parser; titles/labels are drawn plain and stay
      plain textareas. */
  rich?: boolean;
}
interface StrListOpts {
  min?: number;
  max?: number;
  addLabel?: string;
  placeholder?: string;
  rich?: boolean;
}
interface ChildListOpts {
  labelOf: (item: any, i: number) => string;
  tagOf?: (item: any, i: number) => string;
  makeNew?: () => any;
  addLabel?: string;
  min?: number;
  max?: number;
}

// strList "+ add" wants to focus the freshly-added row (the SPA's
// focusLast). bumpForm() changes ContextEditor's key, so it REMOUNTS —
// a component-scoped ref would reset to null before the new textarea
// mounts. Keeping the pending target at module scope lets it survive
// the remount; the matching textarea ref callback consumes and clears
// it. Matched by array identity + index.
let pendingFocus: { arr: any[]; index: number } | null = null;

export default function ContextEditor({
  workingRef,
  selPath,
  markDirty,
  bumpForm,
  selectPath,
}: Props) {
  const autoGrowRef = useCallback((el: HTMLTextAreaElement | null) => {
    if (el) autoGrow(el);
  }, []);

  // DESIGN32 — per-node stash for the NO-outcome mode switch: whatever the
  // switch removes (an authored loop or branch) survives here for the session
  // and is restored verbatim on switch-back, so a stray radio click can never
  // cost the user their typed steps.
  const outcomeStashRef = useRef<Record<string, { loop?: any; branch?: any }>>({});

  /* ── building blocks ─────────────────────────────────── */

  const fGroup = (label: string, ...kids: React.ReactNode[]) => (
    <div className="arch-f-group">
      <span className="arch-f-label">{label}</span>
      {kids.map((k, i) => (
        <React.Fragment key={i}>{k}</React.Fragment>
      ))}
    </div>
  );

  const fHint = (text: string) => <div className="arch-f-hint">{text}</div>;

  const iconBtn = (
    sym: string,
    disabled: boolean,
    onClick: () => void,
    title: string,
  ) => (
    <button
      className={'arch-icon-btn' + (sym !== '✕' ? ' move' : '')}
      type="button"
      disabled={disabled}
      title={title}
      aria-label={title}
      onClick={onClick}
    >
      {sym}
    </button>
  );

  const boundInput = (obj: any, key: string, opts: InputOpts = {}) => (
    <input
      type={opts.type || 'text'}
      defaultValue={obj[key] != null ? obj[key] : ''}
      placeholder={opts.placeholder || ''}
      onInput={(e) => {
        obj[key] = (e.target as HTMLInputElement).value;
        markDirty();
      }}
    />
  );

  const boundTextarea = (obj: any, key: string, opts: InputOpts = {}) =>
    opts.rich ? (
      <RichText
        value={obj[key] != null ? obj[key] : ''}
        placeholder={opts.placeholder}
        onChange={(v) => {
          obj[key] = v;
          markDirty();
        }}
      />
    ) : (
    <textarea
      rows={1}
      placeholder={opts.placeholder || ''}
      defaultValue={obj[key] != null ? obj[key] : ''}
      ref={autoGrowRef}
      onInput={(e) => {
        const ta = e.target as HTMLTextAreaElement;
        obj[key] = ta.value;
        markDirty();
        autoGrow(ta);
      }}
    />
    );

  const strList = (arr: any[], opts: StrListOpts = {}) => {
    const min = opts.min || 0;
    const max = opts.max || 99;
    return (
      <div className="arch-list-editor">
        {arr.map((val, i) => (
          <div className="arch-list-item" key={i}>
            {opts.rich ? (
              <RichText
                value={val != null ? val : ''}
                placeholder={opts.placeholder}
                onReady={(el) => {
                  if (pendingFocus && pendingFocus.arr === arr && pendingFocus.index === i) {
                    pendingFocus = null;
                    el.focus();
                  }
                }}
                onChange={(v) => {
                  arr[i] = v;
                  markDirty();
                }}
              />
            ) : (
            <textarea
              rows={1}
              placeholder={opts.placeholder || ''}
              defaultValue={val != null ? val : ''}
              ref={(el) => {
                if (!el) return;
                autoGrow(el);
                if (pendingFocus && pendingFocus.arr === arr && pendingFocus.index === i) {
                  pendingFocus = null;
                  el.focus();
                }
              }}
              onInput={(e) => {
                const ta = e.target as HTMLTextAreaElement;
                arr[i] = ta.value;
                markDirty();
                autoGrow(ta);
              }}
            />
            )}
            <div className="arch-list-item-controls">
              {iconBtn(
                '↑',
                i === 0,
                () => {
                  swap(arr, i, i - 1);
                  markDirty();
                  bumpForm();
                },
                'Move up',
              )}
              {iconBtn(
                '↓',
                i === arr.length - 1,
                () => {
                  swap(arr, i, i + 1);
                  markDirty();
                  bumpForm();
                },
                'Move down',
              )}
              {iconBtn(
                '✕',
                arr.length <= min,
                () => {
                  arr.splice(i, 1);
                  markDirty();
                  bumpForm();
                },
                'Remove',
              )}
            </div>
          </div>
        ))}
        <button
          className="arch-list-add"
          type="button"
          disabled={arr.length >= max}
          onClick={() => {
            arr.push('');
            pendingFocus = { arr, index: arr.length - 1 };
            markDirty();
            bumpForm();
          }}
        >
          {opts.addLabel || '+ line'}
        </button>
      </div>
    );
  };

  const colorPicker = (obj: any, key: string) => (
    <div className="arch-swatches">
      {Object.entries(PALETTE).map(([token, hex]) => (
        <button
          key={token}
          type="button"
          title={token}
          className={'arch-swatch' + (obj[key] === token ? ' selected' : '')}
          style={{ background: hex }}
          onClick={() => {
            obj[key] = token;
            markDirty();
            bumpForm();
          }}
        />
      ))}
    </div>
  );

  const childList = (arr: any[], basePath: string, opts: ChildListOpts) => {
    const min = opts.min || 0;
    const max = opts.max || 99;
    return (
      <div className="arch-child-list">
        {arr.map((item, i) => (
          <div className="arch-child-row" key={i}>
            {opts.tagOf && opts.tagOf(item, i) ? (
              <span className="arch-c-tag">{opts.tagOf(item, i)}</span>
            ) : null}
            <span
              className="arch-c-title"
              title="Open in editor"
              onClick={() => selectPath(basePath + '.' + i)}
            >
              {opts.labelOf(item, i)}
            </span>
            {iconBtn(
              '↑',
              i === 0,
              () => {
                swap(arr, i, i - 1);
                markDirty();
                bumpForm();
              },
              'Move up',
            )}
            {iconBtn(
              '↓',
              i === arr.length - 1,
              () => {
                swap(arr, i, i + 1);
                markDirty();
                bumpForm();
              },
              'Move down',
            )}
            {iconBtn(
              '✕',
              arr.length <= min,
              () => {
                if (!window.confirm('Delete "' + opts.labelOf(item, i) + '"?'))
                  return;
                arr.splice(i, 1);
                markDirty();
                bumpForm();
              },
              'Remove',
            )}
          </div>
        ))}
        {opts.makeNew ? (
          <button
            className="arch-list-add"
            type="button"
            disabled={arr.length >= max}
            onClick={() => {
              arr.push(opts.makeNew!());
              markDirty();
              selectPath(basePath + '.' + (arr.length - 1));
            }}
          >
            {opts.addLabel || '+ add'}
          </button>
        ) : null}
      </div>
    );
  };

  const backLink = (path: string, label: string) => (
    <button
      className="arch-btn arch-btn-ghost arch-btn-sm"
      type="button"
      onClick={() => selectPath(path)}
    >
      {label}
    </button>
  );

  /* ── forms per kind ──────────────────────────────────── */

  const metaForm = (meta: any) => (
    <>
      {fGroup('Title (H1)', boundInput(meta, 'title', { placeholder: 'Architecture Workflow' }))}
      {fGroup('Subtitle', boundInput(meta, 'subtitle', { placeholder: 'LATO AI for …' }))}
      {fGroup('Phase label', <input
        type="text"
        aria-label="Phase label"
        defaultValue={phaseLabel(meta)}
        maxLength={PHASE_LABEL_LIMIT}
        onInput={event => {
          meta.phase_label = event.currentTarget.value;
          markDirty();
        }}
      />)}
      {fGroup(
        'Phase title',
        boundInput(meta, 'phase_title', {
          placeholder: 'AI-Powered Project, Vendor & Bid Outreach Management System',
        }),
        fHint('Shown on the poster under the header — the general name of the whole phase.'),
      )}
      {fGroup(
        'Phase summary',
        boundTextarea(meta, 'phase_summary', {
          placeholder: 'A centralized AI-driven platform for …',
        }),
      )}
      {fGroup(
        'Footer',
        boundInput(meta, 'footer', {
          placeholder: '© 2026, LATO AI - All rights reserved.',
        }),
        fHint('Leave empty to use the default footer.'),
      )}
    </>
  );

  const legendForm = (legend: any) => {
    const userNode = legend.user ? (
      <>
        {fGroup('USER — label', boundInput(legend.user, 'label', { placeholder: 'USER' }))}
        {fGroup(
          'USER — sublabel',
          boundInput(legend.user, 'sublabel', {
            placeholder: '(TBD in Technical Deep Dive)',
          }),
        )}
        <button
          className="arch-btn arch-btn-danger-ghost arch-btn-sm"
          type="button"
          onClick={() => {
            delete legend.user;
            markDirty();
            bumpForm();
          }}
        >
          Remove USER block
        </button>
      </>
    ) : (
      <button
        className="arch-list-add"
        type="button"
        onClick={() => {
          legend.user = { label: 'USER', sublabel: '' };
          markDirty();
          bumpForm();
        }}
      >
        + USER block
      </button>
    );

    const nucleusNode = Array.isArray(legend.nucleus) ? (
      <>
        {strList(legend.nucleus, { min: 1, max: 2, addLabel: '+ line' })}
        <button
          className="arch-btn arch-btn-danger-ghost arch-btn-sm"
          type="button"
          onClick={() => {
            legend.nucleus = null;
            markDirty();
            bumpForm();
          }}
        >
          Remove AI NUCLEUS
        </button>
      </>
    ) : (
      <button
        className="arch-list-add"
        type="button"
        onClick={() => {
          legend.nucleus = ['AI NUCLEUS', '(LATO OS)'];
          markDirty();
          bumpForm();
        }}
      >
        + AI NUCLEUS block
      </button>
    );

    const chips = ensureArr(legend, 'case_chips');
    const chipsNode = (
      <div>
        {chips.map((chip: any, i: number) => (
          <div className="arch-sub-card" key={i}>
            <div className="arch-sub-card-head">
              <span>{'Chip ' + (i + 1)}</span>
              {iconBtn(
                '✕',
                false,
                () => {
                  chips.splice(i, 1);
                  markDirty();
                  bumpForm();
                },
                'Remove chip',
              )}
            </div>
            {fGroup('Lines', strList(ensureArr(chip, 'lines'), { min: 1, max: 3 }))}
            {fGroup('Color', colorPicker(chip, 'color'))}
          </div>
        ))}
        <button
          className="arch-list-add"
          type="button"
          disabled={chips.length >= 6}
          onClick={() => {
            chips.push(defaultChip());
            markDirty();
            bumpForm();
          }}
        >
          + case chip
        </button>
      </div>
    );

    const blocks = ensureArr(legend, 'blocks');

    return (
      <>
        {fGroup('USER block', <div>{userNode}</div>)}
        {fGroup(
          'Dashboard (1–2 lines)',
          strList(ensureArr(legend, 'dashboard'), { min: 1, max: 2, addLabel: '+ line' }),
        )}
        {fGroup('AI Nucleus', <div>{nucleusNode}</div>)}
        {fGroup('Case chips (left)', chipsNode)}
        {fGroup(
          'Bullet blocks (right)',
          childList(blocks, 'legend.blocks', {
            labelOf: (b: any, i: number) => b.title || 'Block ' + (i + 1),
            makeNew: defaultBlock,
            addLabel: '+ block',
            max: 6,
          }),
          fHint('Click a title to open that block in the editor.'),
        )}
      </>
    );
  };

  const blockForm = (block: any) => (
    <>
      {fGroup('Block title', boundInput(block, 'title'))}
      {fGroup(
        'Bullets',
        strList(ensureArr(block, 'bullets'), { min: 1, max: 12, addLabel: '+ bullet', rich: true }),
        fHint('Bold: select text and press B (or ⌘B / Ctrl+B) — the poster shows it exactly like this.'),
      )}
      {backLink('legend', '← to legend')}
    </>
  );

  const caseForm = (c: any, path: string) => {
    const finNode = c.final ? (
      <button
        className="arch-btn arch-btn-outline arch-btn-sm"
        type="button"
        onClick={() => selectPath(path + '.final')}
      >
        Edit final block
      </button>
    ) : (
      <button
        className="arch-list-add"
        type="button"
        onClick={() => {
          c.final = defaultFinal();
          markDirty();
          selectPath(path + '.final');
        }}
      >
        + final block
      </button>
    );

    return (
      <>
        {fGroup('Case title', boundTextarea(c, 'title', { placeholder: 'CASE 1: …' }))}
        {fGroup('Color', colorPicker(c, 'color'))}
        {fGroup(
          'Intro bullets (1–4)',
          strList(ensureArr(c, 'intro'), { min: 0, max: 4, addLabel: '+ bullet', rich: true }),
        )}
        {fGroup(
          'Pre-lane nodes ([1], [2], …)',
          childList(ensureArr(c, 'nodes'), path + '.nodes', {
            labelOf: nodeTitlePreview,
            makeNew: defaultNode,
            addLabel: '+ node',
            min: 1,
            max: 6,
          }),
        )}
        {fGroup(
          'Lanes (parallel streams)',
          childList(ensureArr(c, 'lanes'), path + '.lanes', {
            labelOf: (l: any, i: number) => l.title || 'Lane ' + (i + 1),
            tagOf: (l: any) => l.tag || '',
            makeNew: defaultLane,
            addLabel: '+ lane',
            min: 0,
            max: 6,
          }),
          fHint('The exception lane must be last. 2–5 stream lanes.'),
        )}
        {fGroup(
          'Final block',
          <div>{finNode}</div>,
          fHint('Required when the case has lanes (standard S9).'),
        )}
      </>
    );
  };

  const finalForm = (f: any, path: string) => {
    const casePath = path.replace(/\.final$/, '');
    return (
      <>
        {fGroup('Title (lines)', strList(ensureArr(f, 'title'), { min: 1, max: 3 }))}
        {fGroup('Color', colorPicker(f, 'color'))}
        {fGroup(
          'Paragraphs',
          strList(ensureArr(f, 'paras'), { min: 0, max: 6, addLabel: '+ paragraph', rich: true }),
        )}
        {fGroup(
          'List (items)',
          strList(ensureArr(f, 'items'), { min: 0, max: 8, addLabel: '+ item', rich: true }),
        )}
        {fGroup('Closing line (tail)', boundTextarea(f, 'tail', { rich: true }))}
        {fGroup('APIs (0–2) — systems this step writes back to', apisEditor(f))}
        <div className="arch-f-group">
          <button
            className="arch-btn arch-btn-danger-ghost arch-btn-sm"
            type="button"
            onClick={() => {
              if (
                !window.confirm(
                  'Delete the final block? It is required when lanes are present.',
                )
              )
                return;
              const r = resolvePath(workingRef.current, casePath);
              if (r && r.value) {
                delete r.value.final;
                markDirty();
                selectPath(casePath);
              }
            }}
          >
            Delete final block
          </button>
        </div>
        {backLink(casePath, '← to case')}
      </>
    );
  };

  /* API stencils — shared by a step and the final block (DESIGN37: the
     closing step may write back into the client's systems). */
  const apisEditor = (owner: any) => {
    const apis = ensureArr(owner, 'apis');
    return (
      <div>
        {apis.map((a: any, i: number) => (
          <div className="arch-api-row" key={i}>
            <input
              type="text"
              defaultValue={a.service || ''}
              placeholder="Service (HCSS…)"
              onInput={(e) => {
                a.service = (e.target as HTMLInputElement).value;
                markDirty();
              }}
            />
            <input
              type="text"
              defaultValue={a.purpose || ''}
              placeholder="Purpose"
              onInput={(e) => {
                a.purpose = (e.target as HTMLInputElement).value;
                markDirty();
              }}
            />
            {iconBtn(
              '✕',
              false,
              () => {
                apis.splice(i, 1);
                markDirty();
                bumpForm();
              },
              'Remove API',
            )}
          </div>
        ))}
        <button
          className="arch-list-add"
          type="button"
          disabled={apis.length >= 2}
          onClick={() => {
            apis.push({ service: '', purpose: '' });
            markDirty();
            bumpForm();
          }}
        >
          + API
        </button>
      </div>
    );
  };

  const nodeForm = (n: any, path: string) => {
    const parentPath = path.replace(/\.nodes\.\d+$/, '');
    const apiNode = apisEditor(n);

    let loopNode: React.ReactNode = null;
    if (n.type === 'decision') {
      // Round 21 — flexible routing: the NO branch may point at any OTHER node
      // in the SAME chain. DESIGN32 adds the second, first-class possibility
      // the client asked for on the video: the NO outcome can carry ITS OWN
      // new steps (a side chain), instead of being forced to loop back or
      // borrow an existing node. The two are one choice — route to existing
      // OR new steps — never both (the validator refuses the pair).
      const chainRes = resolvePath(workingRef.current, parentPath + '.nodes');
      const chain: any[] =
        chainRes && Array.isArray(chainRes.value) ? chainRes.value : [];
      const selfIdx = parseInt(path.split('.').pop() || '-1', 10);
      // A step without an id used to vanish from the pickers entirely (the
      // client: «I don't know why 3A doesn't appear») — routing stores ids, so
      // the picker now OFFERS id-less steps and mints the id at select time.
      const autoIdFor = (nd: any, i: number): string => {
        const fromTitle = String((nd.title || [])[0] || '')
          .match(/\[([A-Za-z0-9-]{1,12})\]/)?.[1];
        let base = (fromTitle || 'step-' + (i + 1)).trim();
        const taken = new Set(
          chain.map((o: any) => (typeof o?.id === 'string' ? o.id.trim() : '')),
        );
        let id = base;
        let k = 2;
        while (taken.has(id)) id = base + '-' + k++;
        return id;
      };
      const ensureId = (idx: number): string => {
        const nd = chain[idx];
        if (typeof nd.id === 'string' && nd.id.trim()) return nd.id.trim();
        nd.id = autoIdFor(nd, idx);
        markDirty();
        return nd.id;
      };
      const AUTO = '__auto__:';
      const optionFor = ({ nd, i }: { nd: any; i: number }) => {
        const hasId = typeof nd.id === 'string' && nd.id.trim() !== '';
        return {
          key: i,
          value: hasId ? (nd.id as string) : AUTO + i,
          label: (hasId ? '[' + nd.id + '] ' : '') + nodeTitlePreview(nd, i),
        };
      };
      const resolvePicked = (val: string): string =>
        val.startsWith(AUTO) ? ensureId(parseInt(val.slice(AUTO.length), 10)) : val;
      const routeOptions = chain
        .map((nd: any, i: number) => ({ nd, i }))
        .filter(({ nd, i }) => i !== selfIdx && nd)
        .map(optionFor);
      // A branch can only JOIN a LATER node — the engine draws the join
      // under the rows and an upward join would cross other steps.
      const joinOptions = chain
        .map((nd: any, i: number) => ({ nd, i }))
        .filter(
          ({ nd, i }) =>
            i > selfIdx &&
            nd &&
            (nd.type === 'box' || nd.type === 'decision' || nd.type === undefined),
        )
        .map(optionFor);
      const currentTo =
        n.loop && typeof n.loop.to === 'string' ? n.loop.to.trim() : '';
      const knownTargets = new Set(routeOptions.map((o) => o.value));
      // DESIGN33 — lanes render side columns too (the engine shifts them left
      // to stay inside the lane frame), so the steps mode is offered
      // everywhere. The one refusal left is the last pre-lane node of a case
      // with lanes (the fan owns that space) — the validator says it in words.
      const mode: 'none' | 'route' | 'steps' = n.branch
        ? 'steps'
        : n.loop
          ? 'route'
          : 'none';
      // A mode switch must never DESTROY authored content (the docx calls
      // deleting branch children out as needing explicit UX): whatever the
      // switch removes is stashed per node path and restored verbatim when
      // the user switches back. The stash is session-local by design — the
      // saved spec carries exactly one of the two, as the validator demands.
      const stash = outcomeStashRef.current;
      const setMode = (m: 'none' | 'route' | 'steps') => {
        const st = stash[path] || (stash[path] = {});
        if (n.loop) st.loop = n.loop;
        if (n.branch) st.branch = n.branch;
        if (m === 'route') {
          delete n.branch;
          if (!n.loop) n.loop = st.loop || { label: 'REMIND', note: '' };
        } else if (m === 'steps') {
          delete n.loop;
          if (!n.branch)
            n.branch = st.branch || { label: 'NO', nodes: [{ title: ['New step'], lines: [] }] };
        } else {
          delete n.loop;
          delete n.branch;
        }
        markDirty();
        bumpForm();
      };

      loopNode = (
        <div>
          <div className="arch-outcome-modes" role="radiogroup" aria-label="NO outcome">
            {(
              [
                ['none', 'None'],
                ['route', 'Goes to an existing step'],
                ['steps', 'Creates new steps'],
              ] as Array<['none' | 'route' | 'steps', string]>
            ).map(([m, label]) => (
              <label className="arch-inline-field" key={m}>
                <input
                  type="radio"
                  name={'outcome-' + path}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {mode === 'route' && n.loop ? (
            <>
              {fGroup('Loop label', boundInput(n.loop, 'label', { placeholder: 'REMIND' }))}
              {fGroup(
                'Note',
                boundInput(n.loop, 'note', { placeholder: 'SMS → Email · ESCALATE' }),
              )}
              {fGroup(
                'NO branch goes to',
                <select
                  defaultValue={currentTo}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) n.loop.to = resolvePicked(val);
                    else delete n.loop.to;
                    markDirty();
                    bumpForm();
                  }}
                >
                  <option value="">Previous step (default)</option>
                  {currentTo && !knownTargets.has(currentTo) ? (
                    <option value={currentTo}>
                      {'[' + currentTo + '] (not in this chain)'}
                    </option>
                  ) : null}
                  {routeOptions.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>,
                fHint(
                  'Route the NO branch to any step in this chain — new steps you add appear here.',
                ),
              )}
              {fGroup('Branch label', boundInput(n.loop, 'branch_label', { placeholder: 'NO' }))}
              {(() => {
                // DESIGN33 — «this arrow should not exist»: when NO routes to a
                // LATER step, offer to detach that step from the linear flow so
                // YES and NO read as two outcomes, never as a sequence.
                const to = typeof n.loop.to === 'string' ? n.loop.to.trim() : '';
                if (!to) return null;
                const tIdx = chain.findIndex(
                  (o: any) => typeof o?.id === 'string' && o.id.trim() === to,
                );
                if (tIdx <= selfIdx) return null;
                const target = chain[tIdx];
                return (
                  <label className="arch-inline-field">
                    <input
                      type="checkbox"
                      checked={!!target.branch_only}
                      onChange={(e) => {
                        if (e.target.checked) target.branch_only = true;
                        else delete target.branch_only;
                        markDirty();
                        bumpForm();
                      }}
                    />
                    <span>
                      {'[' + to + '] is reached ONLY via this branch — remove the arrow from its previous step'}
                    </span>
                  </label>
                );
              })()}
            </>
          ) : null}
          {mode === 'steps' && n.branch ? (
            <>
              {fGroup('Outcome label', boundInput(n.branch, 'label', { placeholder: 'NO' }))}
              {fGroup('Note', boundInput(n.branch, 'note', { placeholder: 'human path' }))}
              {(ensureArr(n.branch, 'nodes') as any[]).map((b: any, i: number) => (
                <div className="arch-sub-card" key={i}>
                  <div className="arch-sub-card-head">
                    <span>{'Step ' + (i + 1)}</span>
                    {iconBtn(
                      '✕',
                      (n.branch.nodes as any[]).length <= 1,
                      () => {
                        (n.branch.nodes as any[]).splice(i, 1);
                        markDirty();
                        bumpForm();
                      },
                      'Remove step',
                    )}
                  </div>
                  {fGroup('Title (1–2 short lines)', strList(ensureArr(b, 'title'), { min: 1, max: 2 }))}
                  {fGroup('Criteria (0–5, drawn inside the step box)', strList(ensureArr(b, 'lines'), { min: 0, max: 5, addLabel: '+ criterion' }))}
                  {fGroup('Color', colorPicker(b, 'color'))}
                </div>
              ))}
              <button
                className="arch-list-add"
                type="button"
                disabled={(n.branch.nodes as any[]).length >= 3}
                onClick={() => {
                  (n.branch.nodes as any[]).push({ title: ['New step'], lines: [] });
                  markDirty();
                  bumpForm();
                }}
              >
                + step in this branch
              </button>
              {fGroup(
                'Then joins (optional)',
                <select
                  defaultValue={typeof n.branch.to === 'string' ? n.branch.to : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) n.branch.to = resolvePicked(val);
                    else delete n.branch.to;
                    markDirty();
                    bumpForm();
                  }}
                >
                  <option value="">Ends on its own</option>
                  {joinOptions.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>,
                fHint(
                  'The branch runs its own steps, then either ends or joins a LATER step in this chain. Only the chosen outcome runs — this is a choice, not a fork.',
                ),
              )}
            </>
          ) : null}
        </div>
      );
    }

    let forkNode: React.ReactNode = null;
    if (n.type === 'fork') {
      const branches = ensureArr(n, 'branches');
      forkNode = (
        <div>
          {branches.map((b: any, i: number) => (
            <div className="arch-sub-card" key={i}>
              <div className="arch-sub-card-head">
                <span>{'Branch ' + (i + 1)}</span>
                {iconBtn(
                  '✕',
                  branches.length <= 2,
                  () => {
                    branches.splice(i, 1);
                    markDirty();
                    bumpForm();
                  },
                  'Remove branch',
                )}
              </div>
              {fGroup('Label on the line', boundInput(b, 'label', { placeholder: 'fix the record' }))}
              {fGroup('Color', colorPicker(b, 'color'))}
              {fGroup('Mini-box lines', strList(ensureArr(b, 'lines'), { min: 1, max: 4 }))}
            </div>
          ))}
          <button
            className="arch-list-add"
            type="button"
            disabled={branches.length >= 2}
            onClick={() => {
              branches.push(defaultBranch());
              markDirty();
              bumpForm();
            }}
          >
            + branch
          </button>
        </div>
      );
    }

    return (
      <>
        {fGroup(
          'Node type',
          <select
            defaultValue={n.type || 'box'}
            onChange={(e) => {
              n.type = e.target.value;
              if (n.type === 'fork' && !Array.isArray(n.branches)) {
                n.branches = [defaultBranch(), defaultBranch()];
              }
              markDirty();
              bumpForm();
            }}
          >
            <option value="box">box — regular block</option>
            <option value="decision">decision — diamond (branch point)</option>
            <option value="fork">fork — block + 2 mini-boxes</option>
          </select>,
        )}
        {fGroup('ID (optional)', boundInput(n, 'id', { placeholder: 'e.g. 3A-1' }))}
        {fGroup('Title (1–3 short lines)', strList(ensureArr(n, 'title'), { min: 1, max: 3 }))}
        {fGroup(
          'Description (right-column bullets, 0–5)',
          strList(ensureArr(n, 'desc'), { min: 0, max: 8, addLabel: '+ bullet', rich: true }),
          fHint('Bold: select text and press B (or ⌘B / Ctrl+B) — the poster shows it exactly like this.'),
        )}
        {fGroup('APIs (0–2)', apiNode)}
        {(() => {
          const selfIdx = parseInt(path.split('.').pop() || '-1', 10);
          if (selfIdx <= 0 || n.type === 'fork') return null;
          return fGroup(
            'Linear flow',
            <label className="arch-inline-field">
              <input
                type="checkbox"
                checked={!!n.branch_only}
                onChange={(e) => {
                  if (e.target.checked) n.branch_only = true;
                  else delete n.branch_only;
                  markDirty();
                  bumpForm();
                }}
              />
              <span>Reached only via a branch — no arrow from the previous step</span>
            </label>,
            fHint(
              'For two outcomes ending in different steps: YES continues to the next step, NO routes here — and the false «sequence» arrow disappears.',
            ),
          );
        })()}
        {n.type === 'decision'
          ? [
              <React.Fragment key="next">
                {fGroup(
                  'Label on the arrow to the next node',
                  boundInput(n, 'next_label', { placeholder: 'YES' }),
                )}
              </React.Fragment>,
              <React.Fragment key="loop">
                {fGroup('NO outcome', loopNode)}
              </React.Fragment>,
            ]
          : null}
        {n.type === 'fork'
          ? fGroup('Fork branches', forkNode, fHint('The fork must be the last node of its lane.'))
          : null}
        {backLink(parentPath, pathKind(parentPath) === 'lane' ? '← to lane' : '← to case')}
      </>
    );
  };

  const laneForm = (l: any, path: string) => {
    const casePath = path.replace(/\.lanes\.\d+$/, '');
    const isException = (l.role || 'stream') === 'exception';
    return (
      <>
        {fGroup('Tag (e.g. 3A)', boundInput(l, 'tag'))}
        {fGroup(
          'Lane title',
          boundTextarea(l, 'title', { placeholder: 'AP — INBOX → QUICKBOOKS · PHASE 1' }),
        )}
        {fGroup('Color', colorPicker(l, 'color'))}
        {fGroup(
          'Role',
          <select
            defaultValue={l.role || 'stream'}
            onChange={(e) => {
              l.role = e.target.value;
              markDirty();
              bumpForm();
            }}
          >
            <option value="stream">stream — regular stream</option>
            <option value="exception">exception — exceptions lane</option>
          </select>,
          fHint('Only one exception lane is allowed, and it is always last.'),
        )}
        {isException
          ? fGroup(
              'Label on the tap (exception_label)',
              boundTextarea(l, 'exception_label', {
                placeholder: 'anything off in any stream → exception',
              }),
            )
          : fGroup(
              'Label in the fan line (fan_label)',
              boundTextarea(l, 'fan_label', {
                placeholder: '[3A] AP — inbox → QuickBooks (Phase 1)',
              }),
            )}
        {fGroup(
          'Intro (1 bullet)',
          strList(ensureArr(l, 'intro'), { min: 0, max: 2, addLabel: '+ bullet', rich: true }),
        )}
        {fGroup(
          'Lane nodes (1–4)',
          childList(ensureArr(l, 'nodes'), path + '.nodes', {
            labelOf: nodeTitlePreview,
            makeNew: defaultNode,
            addLabel: '+ node',
            min: 1,
            max: 6,
          }),
        )}
        {backLink(casePath, '← to case')}
      </>
    );
  };

  const tableForm = (t: any, path: string) => {
    const cols = ensureArr(t, 'columns');
    const rows = ensureArr(t, 'rows');
    const colsNode = (
      <div className="arch-form-columns-grid">
        {cols.map((c: any, i: number) => (
          <div className="arch-col-row" key={i}>
            <input
              type="text"
              className="arch-col-name"
              defaultValue={c.name || ''}
              placeholder="Column"
              onInput={(e) => {
                c.name = (e.target as HTMLInputElement).value;
                markDirty();
              }}
            />
            <input
              type="number"
              className="arch-col-width"
              defaultValue={c.width != null ? c.width : ''}
              placeholder="auto"
              onInput={(e) => {
                const v = parseInt((e.target as HTMLInputElement).value, 10);
                if (v > 0) c.width = v;
                else delete c.width;
                markDirty();
              }}
            />
            {iconBtn(
              '✕',
              cols.length <= 2,
              () => {
                if (
                  !window.confirm(
                    'Delete column "' +
                      (c.name || i + 1) +
                      '"? Its cells will be removed from every row.',
                  )
                )
                  return;
                cols.splice(i, 1);
                rows.forEach((r: any) => {
                  if (Array.isArray(r)) r.splice(i, 1);
                });
                markDirty();
                bumpForm();
              },
              'Remove column',
            )}
          </div>
        ))}
        <button
          className="arch-list-add"
          type="button"
          disabled={cols.length >= 6}
          onClick={() => {
            cols.push({ name: 'Column ' + (cols.length + 1) });
            rows.forEach((r: any) => {
              if (Array.isArray(r)) r.push('');
            });
            markDirty();
            bumpForm();
          }}
        >
          + column
        </button>
      </div>
    );

    return (
      <>
        {fGroup('Table title', boundTextarea(t, 'title'))}
        {fGroup('Columns', colsNode, fHint('The last column width is auto.'))}
        {fGroup(
          'Rows',
          childList(rows, path + '.rows', {
            labelOf: (r: any, i: number) =>
              Array.isArray(r) && r[0] ? String(r[0]) : 'Row ' + (i + 1),
            makeNew: () => cols.map(() => ''),
            addLabel: '+ row',
            min: 1,
            max: 40,
          }),
          fHint('Click a row to edit its cells.'),
        )}
      </>
    );
  };

  const tableRowForm = (row: any, path: string) => {
    const tablePath = path.replace(/\.rows\.\d+$/, '');
    const tr = resolvePath(workingRef.current, tablePath);
    const cols =
      tr && tr.value && Array.isArray(tr.value.columns) ? tr.value.columns : [];
    if (!Array.isArray(row)) {
      return <div className="arch-empty">Row is corrupted.</div>;
    }
    return (
      <>
        {row.map((cell: any, i: number) => (
          <React.Fragment key={i}>
            {fGroup(
              cols[i] && cols[i].name ? cols[i].name : 'Cell ' + (i + 1),
              <RichText
                value={cell != null ? cell : ''}
                onChange={(v) => {
                  row[i] = v;
                  markDirty();
                }}
              />,
            )}
          </React.Fragment>
        ))}
        {backLink(tablePath, '← to table')}
      </>
    );
  };

  /* ── dispatch ────────────────────────────────────────── */

  const kind = pathKind(selPath);
  const r = resolvePath(workingRef.current, selPath);
  if (!kind || !r || r.value === undefined || r.value === null) {
    return <div className="arch-empty">Unknown element.</div>;
  }
  const v = r.value;

  let body: React.ReactNode;
  switch (kind) {
    case 'meta':
      body = metaForm(v);
      break;
    case 'legend':
      body = legendForm(v);
      break;
    case 'block':
      body = blockForm(v);
      break;
    case 'case':
      body = caseForm(v, selPath);
      break;
    case 'final':
      body = finalForm(v, selPath);
      break;
    case 'node':
      body = nodeForm(v, selPath);
      break;
    case 'lane':
      body = laneForm(v, selPath);
      break;
    case 'table':
      body = tableForm(v, selPath);
      break;
    case 'tableRow':
      body = tableRowForm(v, selPath);
      break;
    default:
      body = <div className="arch-empty">Unknown element.</div>;
  }

  return <>{body}</>;
}
